import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A panel anchored above a chip, opened by clicking it.
 *
 * Click rather than hover, deliberately. These panels hold a rendered plan passage or a code
 * snippet — something you read, and sometimes type a note into — not a tooltip you glance at.
 * Opening on hover meant they appeared while you were reaching for something else, and closed
 * while you were reaching for *them*; deciding to open one is now an explicit act, and it stays
 * open until you say otherwise.
 *
 * The trigger is a real `<button>`, so Enter and Space open it too — there is no separate
 * keyboard path to maintain.
 */
export function usePopoverPanel({ align = 'right' }: { align?: 'left' | 'right' } = {}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLElement | null>(null);
  // A callback ref rather than the object: this attaches to a `<span>` in one caller and a
  // `<div>` in the other, and a `RefObject<HTMLDivElement>` is not assignable to a span's ref.
  // Callback refs are contravariant in their parameter, so one taking `HTMLElement` satisfies both.
  const setRoot = useCallback((node: HTMLElement | null) => {
    rootRef.current = node;
  }, []);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  /** Opened by something other than its own trigger — a chip whose note was asked for at the
   * moment its context was attached, somewhere else on the page entirely. */
  const show = useCallback(() => setOpen(true), []);

  // A panel that only closes by finding its trigger again is a trap. Escape, or a click anywhere
  // else, both dismiss it.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // `pointerdown`, not `click`: closing on the press means the panel is gone before whatever
    // was clicked underneath it reacts, rather than both happening in an order that depends on
    // where the pointer went up. The trigger itself is inside `rootRef`, so its own press falls
    // through to `toggle` instead of being handled twice.
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  return {
    open,
    show,
    toggle,
    close,
    triggerProps: { ref: setRoot },
    panelProps: {
      // The edge is a prop rather than something the caller overrides, because two conflicting
      // Tailwind inset utilities in one class string resolve by stylesheet order, not by which
      // was written last — so "append `left-0`" would work or not depending on the build.
      className: `absolute bottom-full z-20 mb-1 ${align === 'left' ? 'left-0' : 'right-0'}`,
    },
  };
}
